// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16)
// vg-binding: INTERFACE-STANDARDS:error-shape
// apps/admin/src/pages/vendors/VendorReviewDrawer.tsx
//
// Phase 1 Task 50 — Side drawer panel showing one vendor's full registration
// + survey + Approve / Reject controls.
//
// Per CONTEXT — vendor approval flow.
// API: GET /admin/vendors/:id (detail) / POST /admin/vendors/:id/approve
//      POST /admin/vendors/:id/reject (rejectionReason 10-2000 chars).
//
// Goals: vendor approval admin drawer with surveys.

import { Fragment, useEffect, useState } from 'react';
import { Button, Spinner, useToast } from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import { useTranslation } from '../../i18n/useTranslation.js';

interface SurveyResponseEntry {
  questionId: string;
  label: string;
  value: string | number | string[];
}

/** 2026-09-09 redesign — registration details from the 2-step register form. */
interface RegistrationDetail {
  businessName: string;
  legalName: string;
  timezone: string;
  language: string;
  shippingMode: string | null;
  techniques: Array<{ code: string; name: Record<string, string> }>;
  warehouses: Array<{
    name: string;
    address: string;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    country: string;
  }>;
  vendorCode: string | null;
}

interface VendorDetail {
  id: string;
  email: string;
  displayName: string;
  phone: string | null;
  countryCode: string;
  status: 'PendingApproval' | 'Active' | 'Inactive';
  appliedAt: string;
  surveyResponses: SurveyResponseEntry[];
  registration: RegistrationDetail | null;
  rejectionReason: string | null;
  rejectedAt: string | null;
  reapplyAvailableAt: string | null;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}
interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

function envelopeError(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error?.message ?? null;
  }
  return null;
}

// ---------------------------------------------------------------------------

export interface VendorReviewDrawerProps {
  vendorId: string;
  onClose: () => void;
  /** Called after Approve or Reject succeeds — parent re-fetches list. */
  onResolved: () => void;
}

export function VendorReviewDrawer({
  vendorId,
  onClose,
  onResolved,
}: VendorReviewDrawerProps): JSX.Element {
  const { toast } = useToast();
  const { t, locale } = useTranslation('vendors');
  const [detail, setDetail] = useState<VendorDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Fetch detail.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      try {
        const client = apiClient();
        const { data, error, response } = await (
          client as unknown as {
            GET: (
              p: string,
            ) => Promise<{
              data?: unknown;
              error?: unknown;
              response: Response;
            }>;
          }
        ).GET(`/api/v1/admin/vendors/${encodeURIComponent(vendorId)}`);
        if (cancelled) return;
        if (error || !response.ok) {
          toast({
            title: t('reviewDrawer.toast.loadFail'),
            description: envelopeError(error) ?? `HTTP ${response.status}`,
            variant: 'error',
          });
          setDetail(null);
          return;
        }
        setDetail(unwrap<VendorDetail>(data));
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t('reviewDrawer.toast.networkError'),
          description: err instanceof Error ? err.message : t('reviewDrawer.toast.unknown'),
          variant: 'error',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [vendorId, toast, t]);

  async function handleApprove(): Promise<void> {
    if (!detail) return;
    setSubmitting(true);
    try {
      const client = apiClient();
      const { error, response } = await (
        client as unknown as {
          POST: (
            p: string,
            init: { body: unknown },
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).POST(`/api/v1/admin/vendors/${encodeURIComponent(detail.id)}/approve`, {
        body: {},
      });
      if (error || !response.ok) {
        toast({
          title: t('reviewDrawer.toast.approveFail'),
          description: envelopeError(error) ?? `HTTP ${response.status}`,
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('reviewDrawer.toast.approveSuccess'),
        description: t('reviewDrawer.toast.approveSuccessDesc', { displayName: detail.displayName }),
        variant: 'success',
      });
      onResolved();
    } catch (err) {
      toast({
        title: t('reviewDrawer.toast.approveFail'),
        description: err instanceof Error ? err.message : t('reviewDrawer.toast.unknown'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReject(): Promise<void> {
    if (!detail) return;
    if (rejectReason.trim().length < 10) {
      toast({
        title: t('reviewDrawer.toast.rejectReasonShort'),
        description: t('reviewDrawer.toast.rejectReasonShortDesc'),
        variant: 'warning',
      });
      return;
    }
    setSubmitting(true);
    try {
      const client = apiClient();
      const { error, response } = await (
        client as unknown as {
          POST: (
            p: string,
            init: { body: unknown },
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).POST(`/api/v1/admin/vendors/${encodeURIComponent(detail.id)}/reject`, {
        body: { rejectionReason: rejectReason.trim() },
      });
      if (error || !response.ok) {
        toast({
          title: t('reviewDrawer.toast.rejectFail'),
          description: envelopeError(error) ?? `HTTP ${response.status}`,
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('reviewDrawer.toast.rejectSuccess'),
        description: t('reviewDrawer.toast.rejectSuccessDesc'),
        variant: 'success',
      });
      onResolved();
    } catch (err) {
      toast({
        title: t('reviewDrawer.toast.rejectFail'),
        description: err instanceof Error ? err.message : t('reviewDrawer.toast.unknown'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('reviewDrawer.ariaLabel')}
      className="fixed inset-0 z-40 flex"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={t('reviewDrawer.closeAriaLabel')}
        onClick={onClose}
        className="flex-1 bg-black/40" /* dark-ok: drawer backdrop scrim, theme-agnostic (P999.11.D-04) */
      />
      {/* Side panel */}
      <aside className="flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-pw-border bg-pw-bg shadow-xl">
        <header className="flex items-center justify-between border-b border-pw-border px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold">{t('reviewDrawer.title')}</h2>
            {detail && (
              <p className="text-xs text-pw-text-muted">{detail.email}</p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('reviewDrawer.close')}
          </Button>
        </header>

        {loading || !detail ? (
          <div className="flex flex-1 items-center justify-center">
            {loading ? <Spinner /> : <p>{t('reviewDrawer.notFound')}</p>}
          </div>
        ) : (
          <div className="flex flex-1 flex-col gap-5 px-5 py-4">
            <section>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-pw-text-muted">
                {t('reviewDrawer.profile.heading')}
              </h3>
              <dl className="grid grid-cols-3 gap-2 text-sm">
                <dt className="text-pw-text-muted">{t('reviewDrawer.profile.displayName')}</dt>
                <dd className="col-span-2">{detail.displayName}</dd>
                <dt className="text-pw-text-muted">{t('reviewDrawer.profile.phone')}</dt>
                <dd className="col-span-2">{detail.phone ?? '—'}</dd>
                <dt className="text-pw-text-muted">{t('reviewDrawer.profile.country')}</dt>
                <dd className="col-span-2">{detail.countryCode}</dd>
                <dt className="text-pw-text-muted">{t('reviewDrawer.profile.status')}</dt>
                <dd className="col-span-2">{detail.status}</dd>
                <dt className="text-pw-text-muted">{t('reviewDrawer.profile.applied')}</dt>
                <dd className="col-span-2">
                  {new Date(detail.appliedAt).toLocaleString()}
                </dd>
                {detail.rejectionReason && (
                  <>
                    <dt className="text-pw-text-muted">{t('reviewDrawer.profile.lastReject')}</dt>
                    <dd className="col-span-2 text-red-600 dark:text-red-300">
                      {detail.rejectionReason}
                    </dd>
                  </>
                )}
              </dl>
            </section>

            {detail.registration ? (
              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-pw-text-muted">
                  {t('reviewDrawer.registration.heading')}
                </h3>
                <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
                  <dt className="text-pw-text-muted">
                    {t('reviewDrawer.registration.businessName')}
                  </dt>
                  <dd className="col-span-2 text-pw-text">
                    {detail.registration.businessName}
                  </dd>
                  <dt className="text-pw-text-muted">
                    {t('reviewDrawer.registration.legalName')}
                  </dt>
                  <dd className="col-span-2 text-pw-text">
                    {detail.registration.legalName}
                  </dd>
                  <dt className="text-pw-text-muted">
                    {t('reviewDrawer.registration.timezone')}
                  </dt>
                  <dd className="col-span-2 text-pw-text">
                    {detail.registration.timezone}
                  </dd>
                  <dt className="text-pw-text-muted">
                    {t('reviewDrawer.registration.shippingMode')}
                  </dt>
                  <dd className="col-span-2 text-pw-text">
                    {detail.registration.shippingMode
                      ? t(
                          `createSteps.locationCapabilities.shippingMode.options.${detail.registration.shippingMode}`,
                        )
                      : '—'}
                  </dd>
                  <dt className="text-pw-text-muted">
                    {t('reviewDrawer.registration.techniques')}
                  </dt>
                  <dd className="col-span-2 text-pw-text">
                    {detail.registration.techniques.length === 0 ? (
                      '—'
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {detail.registration.techniques.map((tech) => (
                          <span
                            key={tech.code}
                            className="rounded-full bg-pw-surface px-2 py-0.5 text-xs"
                          >
                            {tech.name?.[locale] ?? tech.name?.en ?? tech.code}
                          </span>
                        ))}
                      </span>
                    )}
                  </dd>
                  {detail.registration.warehouses.map((wh, idx) => (
                    <Fragment key={`${wh.name}-${idx}`}>
                      <dt className="text-pw-text-muted">
                        {t('reviewDrawer.registration.warehouse')}
                      </dt>
                      <dd className="col-span-2 text-pw-text">
                        <span className="font-medium">{wh.name}</span>
                        <span className="block text-pw-text-muted">
                          {wh.address} · {wh.country}
                        </span>
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              </section>
            ) : null}

            <section>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-pw-text-muted">
                {t('reviewDrawer.survey.heading')}
              </h3>
              {detail.surveyResponses.length === 0 ? (
                <p className="text-sm text-pw-text-muted">
                  {t('reviewDrawer.survey.empty')}
                </p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {detail.surveyResponses.map((q) => (
                    <li key={q.questionId} className="rounded-md bg-pw-surface p-3">
                      <p className="text-xs font-medium text-pw-text-muted">
                        {q.label}
                      </p>
                      <p className="mt-1 text-pw-text">
                        {Array.isArray(q.value)
                          ? q.value.join(', ')
                          : String(q.value)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {detail.status === 'PendingApproval' && (
              <section className="mt-auto border-t border-pw-border pt-4">
                {!rejectMode ? (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="destructive"
                      onClick={() => setRejectMode(true)}
                      disabled={submitting}
                    >
                      {t('reviewDrawer.actions.reject')}
                    </Button>
                    <Button
                      variant="default"
                      onClick={handleApprove}
                      disabled={submitting}
                    >
                      {t('reviewDrawer.actions.approve')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-pw-text">
                      {t('reviewDrawer.actions.rejectReasonLabel')}
                    </label>
                    <textarea
                      className="block h-28 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder={t('reviewDrawer.actions.rejectReasonPlaceholder')}
                      disabled={submitting}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setRejectMode(false);
                          setRejectReason('');
                        }}
                        disabled={submitting}
                      >
                        {t('reviewDrawer.actions.cancel')}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleReject}
                        disabled={submitting || rejectReason.trim().length < 10}
                      >
                        {t('reviewDrawer.actions.confirmReject')}
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
